import { Dialog, DialogContent, DialogTitle, Grid2, IconButton, Typography } from '@mui/material';

import { CloseOutlined } from '@mui/icons-material';
import JobSiteDrawerForm from './JobSiteDrawerForm';
import { useJobSiteFormData } from '@/jobSites/presentation/hooks/useJobSiteFormData';
import { useMemo } from 'react';
import tid from '@/common/infrastructure/config/tid';

interface ModalProps {
  open: boolean;
  selectedJobSiteId?: number;
  selectedCustomerId?: number;
  selectedCustomerCompanyName?: string;
  onClose: () => void;
  onInputActionClicked?: () => void;
  isNested?: boolean; // Indicates if this modal is opened inside another modal
}

export default function JobSiteModal({
  open,
  onClose,
  selectedJobSiteId,
  selectedCustomerId,
  selectedCustomerCompanyName,
  onInputActionClicked,
  isNested = false
}: Readonly<ModalProps>) {
  const { isNew, jobSite, jobSiteLoading, formOptionsLoading } = useJobSiteFormData(selectedJobSiteId, !open);

  // Merge customer info into initial values when creating a new job site
  const initialValuesWithCustomer = useMemo(() => {
    if (isNew && selectedCustomerId && selectedCustomerCompanyName) {
      return {
        ...jobSite,
        customerId: selectedCustomerId,
        customerCompanyName: selectedCustomerCompanyName
      };
    }
    return jobSite;
  }, [isNew, jobSite, selectedCustomerId, selectedCustomerCompanyName]);

  const formikStatus = useMemo(() => ({ loading: jobSiteLoading || formOptionsLoading }), [jobSiteLoading, formOptionsLoading]);

  const title = isNew ? 'Create New JobSite' : 'JobSite Details';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      sx={{
        // Increase z-index when nested to appear above parent modal
        zIndex: isNested ? 1400 : 1300,
        '& .MuiDialog-paper': {
          width: '80vw !important',
          maxWidth: '1200px !important',
          maxHeight: '90vh',
          minWidth: '800px'
        }
      }}
    >
      <DialogTitle sx={{ padding: 2, paddingBottom: 1 }}>
        <Grid2 container alignItems="center" justifyContent="space-between">
          <Grid2>
            <Typography variant="h6">{title}</Typography>
          </Grid2>
          <Grid2>
            <IconButton onClick={onClose} size="small" {...tid('job-sites:management:modal:close-button')}>
              <CloseOutlined />
            </IconButton>
          </Grid2>
        </Grid2>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <JobSiteDrawerForm
          loading={jobSiteLoading}
          isNew={isNew}
          initialValues={initialValuesWithCustomer}
          formikStatus={formikStatus}
          onClose={onClose}
          onInputActionClicked={onInputActionClicked}
          open={open}
        />
      </DialogContent>
    </Dialog>
  );
}
